import { FC, useContext } from "react";

import Dialog from "~/components/Dialog/Dialog";
import Button from "~/components/Button/Button";
import DealBuilderContext from "~/contexts/DealBuilderContext";
import OrderContext from "~/contexts/OrderContext";
import TaxContext from "~/contexts/TaxContext";
import { CartLine } from "~/entities/orderEntities";
import { DealGroup } from "~/helpers/bundleDisplay";
import { withIndefiniteArticle } from "~/helpers/dealHelpers";

import styles from "./RemoveDealItemSheet.module.css";

type RemoveDealItemSheetProps = {
  target: { deal: DealGroup; line: CartLine } | null;
  onClose: VoidFunction;
};

const joinWords = (words: string[]) =>
  words.length <= 1
    ? words.join("")
    : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

// Taking one item out of a deal: swap it, remove just it (losing the saving), or remove the whole deal
const RemoveDealItemSheet: FC<RemoveDealItemSheetProps> = ({
  target,
  onClose,
}) => {
  const { deals, openDealEditor } = useContext(DealBuilderContext);
  const { removeOrderLine, removeOrderLines } = useContext(OrderContext);
  const { formatAmount } = useContext(TaxContext);

  const deal =
    target && deals.find((x) => x.code === target.deal.promotionCode);
  const slotLabel = (line: CartLine) =>
    deal?.slots
      .find((s) =>
        s.options.some((o) => o.menuItem.productCode === line.item.productCode),
      )
      ?.label.toLowerCase();

  const name = target?.line.item.product.name ?? "";
  const slot = target && slotLabel(target.line);
  const others = target
    ? target.deal.members
        .filter((m) => m.line.id !== target.line.id)
        .map((m) => slotLabel(m.line) ?? m.line.item.product.name)
    : [];

  return (
    <Dialog title={`Remove ${name}?`} visible={!!target} onClose={onClose}>
      {target && (
        <div className={styles.sheet}>
          <p className={styles.body}>
            {name} is part of your {target.deal.name}.{" "}
            {slot
              ? `Without ${withIndefiniteArticle(slot)}, the ${joinWords(others)} go back to full price.`
              : "Without it, the other items go back to full price."}
          </p>
          {deal && slot && (
            <Button
              onClick={() => {
                openDealEditor(
                  target.deal.promotionCode,
                  target.deal.members.map((m) => m.line),
                  target.line.id,
                );
                onClose();
              }}
            >
              Swap for another {slot}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              removeOrderLine(target.line.id);
              onClose();
            }}
          >
            Remove {name} only · +{formatAmount(target.deal.saving)}
          </Button>
          <button
            type="button"
            className={styles.removeAll}
            onClick={() => {
              removeOrderLines(target.deal.members.map((m) => m.line.id));
              onClose();
            }}
          >
            Remove the whole {target.deal.name}
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default RemoveDealItemSheet;
